/* eslint-disable max-lines */
import { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { formatCurrency } from '@utils/helpers/currency';
import SelectInput from '@components/atoms/ui-kit/select-input';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import {
  Package,
  CreditCard,
  Layers,
  Zap,
  AlignLeft,
  Languages,
  FileText,
  GraduationCap,
  Shield,
  Bell,
  Share2,
  Check,
  Info
} from 'lucide-react';
import { WizardFormData } from './create-plan-wizard-types';
import { PlanType } from '@utils/types/back-office/update-plan-bo';

interface PricePlan {
  id: number;
  name: string;
}

interface Reseller {
  id: number | string;
  name: string;
}

interface Props {
  watchedValues: WizardFormData;
  planType: PlanType;
  pricePlans: PricePlan[];
  resellers: Reseller[];
  setResellerSearchQuery: (q: string) => void;
  setValue: UseFormSetValue<WizardFormData>;
  currency: 'EUR' | 'USD';
}

function ReviewSection({
  icon: Icon,
  title,
  children
}: {
  icon: typeof Package;
  title: string;
  children: React.ReactNode;
}) {
  const { getColour } = useThemeContext();
  return (
    <div
      className="p-4 rounded-2xl border"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('secondLayer')
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={13} style={{ color: getColour('inactiveText') }} />
        <h4
          className="text-xs font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
}

function TierList({
  tiers,
  currency
}: {
  tiers: { start: number; end: number | null; price_per_unit: string }[];
  currency: 'EUR' | 'USD';
}) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  return (
    <div
      className="mt-3 pt-3 border-t"
      style={{ borderColor: getColour('border') }}
    >
      <h5
        className="text-xs font-semibold mb-2"
        style={{ color: getColour('normalText') }}
      >
        {t('pricing-tiers')}
      </h5>
      <div className="space-y-1">
        {tiers.map((tier, index) => (
          <div
            key={index}
            className="flex items-center justify-between text-xs px-3 py-1.5 rounded-xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <span style={{ color: getColour('inactiveText') }}>
              {tier.start} - {tier.end === null ? t('unlimited') : tier.end}
            </span>
            <span
              className="font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {formatCurrency(Number(tier.price_per_unit), currency)}/
              {t('unit')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function YesNoBadge({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return value ? (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400">
      {t('yes')}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-zinc-600/10 text-zinc-700 dark:bg-white/5 dark:text-zinc-400">
      {t('no')}
    </span>
  );
}

export function WizardStep3Review({
  watchedValues,
  planType,
  pricePlans,
  resellers,
  setResellerSearchQuery,
  setValue,
  currency
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const selectedSttPricePlan = pricePlans.find(
    p => p.id === watchedValues.stt_pricing_plan
  );
  const selectedAlignmentPricePlan = pricePlans.find(
    p => p.id === watchedValues.alignment_pricing_plan
  );

  const enabledFeatures = [
    watchedValues.batch_enabled && t('batch-processing'),
    watchedValues.realtime_enabled && t('real-time-processing'),
    watchedValues.alignment_enabled && t('alignment'),
    watchedValues.translation_enabled && t('translation'),
    watchedValues.templates_exported_enabled && t('export-templates'),
    watchedValues.automatic_training_enabled && t('automatic-training')
  ].filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 pb-1">
        <Check size={14} style={{ color: getColour('activeText') }} />
        <p
          className="text-sm font-medium"
          style={{ color: getColour('normalText') }}
        >
          {t('review-plan-details')}
        </p>
      </div>

      {/* Plan Info */}
      <ReviewSection icon={Package} title={t('plan-info')}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('type')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            <StatusBadge status={planType} label={planType} />
          </dd>
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('sku')}:
          </dt>
          <dd
            className="font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {watchedValues.sku}
          </dd>
          {watchedValues.description && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('description')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.description}
              </dd>
            </>
          )}
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('processing-type')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.processing_type}
          </dd>
        </dl>
      </ReviewSection>

      {/* Features */}
      <ReviewSection
        icon={Layers}
        title={`${t('features')} (${enabledFeatures.length})`}
      >
        {enabledFeatures.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {enabledFeatures.map((feature, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/15 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
              >
                <Check size={10} />
                {feature}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('no-features-enabled')}
          </p>
        )}
      </ReviewSection>

      {/* Plan Cost */}
      <ReviewSection icon={CreditCard} title={t('plan-cost')}>
        {currency === 'USD' && (
          <div className="flex items-start gap-1.5 mb-3 rounded-2xl bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 px-2.5 py-1.5">
            <Info className="h-3 w-3 mt-0.5 shrink-0 text-blue-500" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Prices shown in USD; amounts entered are the same numeric value,
              no conversion.
            </p>
          </div>
        )}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          {planType === 'Bundle' && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('monthly-cost')}:
              </dt>
              <dd
                className="font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {watchedValues.monthly_cost
                  ? formatCurrency(Number(watchedValues.monthly_cost), currency)
                  : '-'}
              </dd>
            </>
          )}
          {planType === 'Perpetual' && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('cost')}:
              </dt>
              <dd
                className="font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {watchedValues.cost
                  ? formatCurrency(Number(watchedValues.cost), currency)
                  : '-'}
              </dd>
            </>
          )}
          {planType === 'Pay as you Go' && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('minimal-invoice')}:
              </dt>
              <dd
                className="font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {watchedValues.minimal_invoice
                  ? formatCurrency(
                      Number(watchedValues.minimal_invoice),
                      currency
                    )
                  : '-'}
              </dd>
            </>
          )}
        </dl>
      </ReviewSection>

      {/* STT Pricing */}
      {(watchedValues.batch_enabled || watchedValues.realtime_enabled) && (
        <ReviewSection icon={Zap} title={t('stt-pricing')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            {watchedValues.stt_pricing_mode === 'existing' &&
              selectedSttPricePlan && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-plan-name')}:
                  </dt>
                  <dd style={{ color: getColour('normalText') }}>
                    {selectedSttPricePlan.name}
                  </dd>
                </>
              )}
            {watchedValues.stt_pricing_mode === 'inline' &&
              watchedValues.stt_pricing_plan_name && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-plan-name')}:
                  </dt>
                  <dd style={{ color: getColour('normalText') }}>
                    {watchedValues.stt_pricing_plan_name} ({t('inline-tiers')})
                  </dd>
                </>
              )}
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('media-unit-size')}:
            </dt>
            <dd style={{ color: getColour('normalText') }}>
              {watchedValues.media_unit_size || 60}
            </dd>
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('stt-limit')}:
            </dt>
            <dd style={{ color: getColour('normalText') }}>
              {watchedValues.stt_limit || t('unlimited')}
            </dd>
            {watchedValues.realtime_enabled && (
              <>
                <dt
                  className="font-medium"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('max-concurrent-realtime-streams')}:
                </dt>
                <dd style={{ color: getColour('normalText') }}>
                  {watchedValues.realtime_max_concurrent_streams ||
                    t('unlimited')}
                </dd>
                <dt
                  className="font-medium"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('allowed-realtime-languages')}:
                </dt>
                <dd style={{ color: getColour('normalText') }}>
                  {watchedValues.realtime_languages_csv?.trim()
                    ? watchedValues.realtime_languages_csv
                    : t('all-languages')}
                </dd>
              </>
            )}
          </dl>
          {watchedValues.stt_pricing_mode === 'inline' &&
            watchedValues.stt_pricing_tiers.length > 0 && (
              <TierList
                tiers={watchedValues.stt_pricing_tiers}
                currency={currency}
              />
            )}
        </ReviewSection>
      )}

      {/* Alignment Pricing */}
      {watchedValues.alignment_enabled && (
        <ReviewSection icon={AlignLeft} title={t('alignment-pricing')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            {watchedValues.alignment_pricing_mode === 'existing' &&
              selectedAlignmentPricePlan && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-plan-name')}:
                  </dt>
                  <dd style={{ color: getColour('normalText') }}>
                    {selectedAlignmentPricePlan.name}
                  </dd>
                </>
              )}
            {watchedValues.alignment_pricing_mode === 'inline' &&
              watchedValues.alignment_pricing_plan_name && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-plan-name')}:
                  </dt>
                  <dd style={{ color: getColour('normalText') }}>
                    {watchedValues.alignment_pricing_plan_name} (
                    {t('inline-tiers')})
                  </dd>
                </>
              )}
          </dl>
          {watchedValues.alignment_pricing_mode === 'inline' &&
            watchedValues.alignment_pricing_tiers.length > 0 && (
              <TierList
                tiers={watchedValues.alignment_pricing_tiers}
                currency={currency}
              />
            )}
        </ReviewSection>
      )}

      {/* Translation Pricing */}
      {watchedValues.translation_enabled && (
        <ReviewSection icon={Languages} title={t('translation-pricing')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            {watchedValues.translation_pricing_mode === 'existing' &&
              watchedValues.translation_pricing_plan && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('translation-price-plan')}:
                  </dt>
                  <dd
                    className="font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {pricePlans.find(
                      p => p.id === watchedValues.translation_pricing_plan
                    )?.name || `#${watchedValues.translation_pricing_plan}`}
                  </dd>
                </>
              )}
            {watchedValues.translation_pricing_mode === 'inline' &&
              watchedValues.translation_pricing_plan_name && (
                <>
                  <dt
                    className="font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-plan-name')}:
                  </dt>
                  <dd
                    className="font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {watchedValues.translation_pricing_plan_name} (
                    {t('inline-tiers')}:{' '}
                    {watchedValues.translation_pricing_tiers.length})
                  </dd>
                </>
              )}
            {watchedValues.translation_pricing_mode === 'flat' && (
              <>
                <dt
                  className="font-medium"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('cost-per-unit')}:
                </dt>
                <dd
                  className="font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {watchedValues.translation_cost
                    ? formatCurrency(
                        Number(watchedValues.translation_cost),
                        currency
                      )
                    : '-'}
                </dd>
                <dt
                  className="font-medium"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('characters-per-unit')}:
                </dt>
                <dd style={{ color: getColour('normalText') }}>
                  {watchedValues.translation_characters || '-'}
                </dd>
              </>
            )}
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('translation-character-limit')}:
            </dt>
            <dd style={{ color: getColour('normalText') }}>
              {watchedValues.translation_character_limit || '-'}
            </dd>
          </dl>
        </ReviewSection>
      )}

      {/* Exceedance Email Alerts */}
      <ReviewSection icon={Bell} title={t('exceedance-email-alerts')}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('send-exceedance-email')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            <YesNoBadge value={watchedValues.send_exceedance_email ?? false} />
          </dd>
          {watchedValues.send_exceedance_email && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('exceedance-email-recipients')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.exceedance_email_recipients || '-'}
              </dd>
            </>
          )}
        </dl>
      </ReviewSection>

      {/* Training Pricing */}
      {watchedValues.automatic_training_enabled && (
        <ReviewSection icon={GraduationCap} title={t('training-pricing')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('unit-cost')}:
            </dt>
            <dd
              className="font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {watchedValues.automatic_training_unit_cost
                ? formatCurrency(
                    Number(watchedValues.automatic_training_unit_cost),
                    currency
                  )
                : '-'}
            </dd>
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('monthly-cost')}:
            </dt>
            <dd
              className="font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {watchedValues.automatic_training_monthly_cost
                ? formatCurrency(
                    Number(watchedValues.automatic_training_monthly_cost),
                    currency
                  )
                : '-'}
            </dd>
          </dl>
        </ReviewSection>
      )}

      {/* Template Pricing */}
      {watchedValues.templates_exported_enabled && (
        <ReviewSection icon={FileText} title={t('template-pricing')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <dt
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('export-cost')}:
            </dt>
            <dd
              className="font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {watchedValues.stt_templates_exported_cost
                ? formatCurrency(
                    Number(watchedValues.stt_templates_exported_cost),
                    currency
                  )
                : '-'}
            </dd>
          </dl>
        </ReviewSection>
      )}

      {/* Fair Use / Usage Limits */}
      <ReviewSection icon={Shield} title={t('fair-use')}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('enable-fair-use-mode')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.is_fair_use ? (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400">
                {t('enabled')}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-zinc-600/10 text-zinc-700 dark:bg-white/5 dark:text-zinc-400">
                {t('disabled')}
              </span>
            )}
          </dd>
          {watchedValues.is_fair_use && (
            <>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('rate-limit')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.fair_use_rate_limit
                  ? `${watchedValues.fair_use_rate_limit} ${t('requests-per-minute')}`
                  : t('unlimited')}
              </dd>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('training-limit')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.automatic_training_unit_limit
                  ? `${watchedValues.automatic_training_unit_limit} ${t('per-period')}`
                  : t('unlimited')}
              </dd>
              <dt
                className="font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('hosting-limit')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.automatic_training_hosting_limit
                  ? `${watchedValues.automatic_training_hosting_limit} ${t('models')}`
                  : t('unlimited')}
              </dd>
            </>
          )}
          <dt
            className="font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('custom-models-per-year')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.custom_model_yearly_limit
              ? `${watchedValues.custom_model_yearly_limit} ${t('per-year')}`
              : t('unlimited')}
          </dd>
        </dl>
      </ReviewSection>

      {/* Reseller Publication */}
      <ReviewSection
        icon={Share2}
        title={`${t('publish-to-resellers')} (${t('optional')})`}
      >
        <SelectInput
          isMulti
          options={resellers.map(r => ({
            label: r.name,
            value: String(r.id)
          }))}
          value={watchedValues.reseller_ids.map(id => {
            const reseller = resellers.find(r => Number(r.id) === id);
            return {
              label: reseller?.name || `Reseller #${id}`,
              value: String(id)
            };
          })}
          onChange={selected => {
            const selectedArray = selected as { value: string }[] | null;
            setValue(
              'reseller_ids',
              selectedArray?.map(s => Number(s.value)) || []
            );
          }}
          placeholder={t('search-and-select-resellers')}
          onInputChange={inputValue => setResellerSearchQuery(inputValue)}
          menuPlacement="top"
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </ReviewSection>
    </div>
  );
}
